import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';

export interface TemplateColumnConfig {
  name: string;
  required?: boolean;
  description?: string;
}

export interface ModuleTemplateConfig {
  module: string;
  columns: TemplateColumnConfig[];
  defaultColumns?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  
  // Default column configurations for each module
  private moduleTemplates: Record<string, TemplateColumnConfig[]> = {
    'vendor': [
      { name: 'company_name', required: true },
      { name: 'custom_form', required: false, description: 'Form template ID or name (optional, uses default if not provided)' },
      { name: 'type', required: false },
      { name: 'category', required: false },
      { name: 'vendor_name', required: false },
      { name: 'vendor_type', required: false },
      { name: 'vendor_group', required: false },
      { name: 'vendor_email', required: false },
      { name: 'vendor_phone', required: false },
      { name: 'alt_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'tax_identification_number', required: false },
      { name: 'tan_number', required: false },
      { name: 'registration_type', required: false },
      { name: 'registration_number', required: false },
      { name: 'msme', required: false },
      { name: 'msme_number', required: false },
      { name: 'msme_category', required: false },
      { name: 'legal_name', required: false },
      { name: 'default_payable_account', required: true },
      { name: 'default_expense_account', required: false },
      { name: 'terms', required: false },
      { name: 'credit_limit', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false },
      { name: 'bank_account_type', required: false },
      { name: 'bank_ifsc_code', required: false },
      { name: 'bank_branch', required: false },
      { name: 'bank_state', required: false },
      { name: 'bank_country', required: false },
      { name: 'gst_number', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city', required: false },
      { name: 'state', required: false },
      { name: 'country', required: false },
      { name: 'address_shipping', required: false },
      { name: 'pin_shipping', required: false },
      { name: 'city_shipping', required: false },
      { name: 'state_shipping', required: false },
      { name: 'country_shipping', required: false },
      { name: 'opening_balance', required: false },
      { name: 'opening_balance_date', required: false },
      { name: 'opening_balance_account', required: false },
      { name: 'contact', required: false },
      { name: 'job_title', required: false },
      { name: 'email', required: false },
      { name: 'phone', required: false },
      { name: 'subsidiary', required: true }
    ],
    // Vendor bulk update template - code/id required for identification, all other fields optional
    'vendor-update': [
      { name: 'code', required: true, description: 'Vendor code (required identifier). Use this OR id to identify the vendor.' },
      { name: 'id', required: false, description: 'Vendor ID (optional identifier). Takes priority over code if provided.' },
      { name: 'company_name', required: false, description: 'Leave empty to keep current value. Use __CLEAR__ to set to NULL.' },
      { name: 'custom_form', required: false, description: 'Form template ID or name' },
      { name: 'type', required: false, description: 'Vendor type name (e.g., Company, Individual)' },
      { name: 'category', required: false, description: 'Vendor category name' },
      { name: 'project', required: false, description: 'Project name' },
      { name: 'product', required: false, description: 'Product name' },
      { name: 'product_category', required: false, description: 'Product Category name' },
      { name: 'vendor_group', required: false },
      { name: 'vendor_email', required: false },
      { name: 'vendor_phone', required: false },
      { name: 'alt_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'tax_identification_number', required: false },
      { name: 'tan_number', required: false },
      { name: 'registration_number', required: false },
      { name: 'msme', required: false },
      { name: 'msme_number', required: false },
      { name: 'msme_category', required: false },
      { name: 'legal_name', required: false },
      { name: 'default_payable_account', required: false },
      { name: 'default_expense_account', required: false },
      { name: 'terms', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false, description: 'Bank name (resolved to ID)' },
      { name: 'bank_account_type', required: false },
      { name: 'bank_ifsc_code', required: false },
      { name: 'bank_branch', required: false },
      { name: 'bank_state', required: false },
      { name: 'bank_country', required: false },
      { name: 'gst_number', required: false, description: 'Updates existing tax info if GST matches, otherwise creates new record' },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city', required: false },
      { name: 'state', required: false },
      { name: 'country', required: false },
      { name: 'address_shipping', required: false },
      { name: 'pin_shipping', required: false },
      { name: 'city_shipping', required: false },
      { name: 'state_shipping', required: false },
      { name: 'country_shipping', required: false },
      { name: 'opening_balance', required: false },
      { name: 'opening_balance_date', required: false, description: 'Date format: DD/MM/YYYY' },
      { name: 'opening_balance_account', required: false, description: 'Account code or name' },
      { name: 'subsidiary', required: false, description: 'Subsidiary name. Updates existing classification or creates new' },
      { name: 'credit_limit', required: false, description: 'Classification credit limit (updates existing subsidiary row)' },
      { name: 'is_primary', required: false },
      { name: 'contact', required: false, description: 'Contact name. Updates existing contact if email/phone matches' },
      { name: 'job_title', required: false },
      { name: 'email', required: false },
      { name: 'phone', required: false },
      { name: 'role', required: false, description: 'Contact role name (resolved to ID)' }
    ],
    'employee': [
      { name: 'company_name', required: true },
      { name: 'employee_code', required: false, description: 'Employee Code' },
      { name: 'custom_form', required: false, description: 'Form template ID or name (optional, uses default if not provided)' },
      { name: 'designation', required: false },
      { name: 'department', required: false },
      { name: 'role', required: false },
      { name: 'reporting_to', required: false },
      { name: 'line_of_business', required: false, description: 'Line of Business name' },
      { name: 'product', required: false, description: 'Product name' },
      { name: 'product_category', required: false, description: 'Product Category name' },
      { name: 'cost_center', required: false, description: 'Cost Center name' },
      { name: 'employee_email', required: false, description: 'Employee Email' },
      { name: 'employee_phone', required: false, description: 'Employee Phone' },
      { name: 'alt_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'default_payable_account', required: true },
      { name: 'default_expense_account', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false },
      { name: 'bank_account_type', required: false },
      { name: 'bank_ifsc_code', required: false },
      { name: 'bank_branch', required: false },
      { name: 'bank_state', required: false },
      { name: 'bank_country', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city', required: false },
      { name: 'state', required: false },
      { name: 'country', required: false },
      { name: 'credit_limit', required: false },
      { name: 'subsidiary', required: true }
    ],
    'employee-update': [
      { name: 'employee_code', required: true, description: 'Employee code (required identifier). Use this OR id to identify the employee.' },
      { name: 'id', required: false, description: 'Employee ID (optional identifier). Takes priority over code if provided.' },
      { name: 'company_name', required: false, description: 'Leave empty to keep current value. Use __CLEAR__ to set to NULL.' },
      { name: 'custom_form', required: false, description: 'Form template ID or name' },
      { name: 'designation', required: false },
      { name: 'department', required: false },
      { name: 'role', required: false },
      { name: 'reporting_to', required: false },
      { name: 'line_of_business', required: false, description: 'Line of Business name' },
      { name: 'product', required: false, description: 'Product name' },
      { name: 'product_category', required: false, description: 'Product Category name' },
      { name: 'cost_center', required: false, description: 'Cost Center name' },
      { name: 'employee_email', required: false, description: 'Employee Email' },
      { name: 'employee_phone', required: false, description: 'Employee Phone' },
      { name: 'alt_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'default_payable_account', required: false },
      { name: 'default_expense_account', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false },
      { name: 'bank_account_type', required: false },
      { name: 'bank_ifsc_code', required: false },
      { name: 'bank_branch', required: false },
      { name: 'bank_state', required: false },
      { name: 'bank_country', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city', required: false },
      { name: 'state', required: false },
      { name: 'country', required: false },
      { name: 'credit_limit', required: false },
      { name: 'subsidiary', required: false, description: 'If provided, creates new classification (skips if duplicate)' }
    ],
    'vendor-po': [
      { name: 'PO Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'PO Date', required: true },
      { name: 'Expected Delivery Date', required: false },
      { name: 'Item Name', required: true },
      { name: 'Quantity', required: true },
      { name: 'Unit Price', required: true },
      { name: 'Total Amount', required: false },
      { name: 'Tax Amount', required: false }
    ],
    'vendor-invoice': [
      // Header Level Fields (A-Q)
      { name: 'id', required: false, description: 'Internal ID for updates' },
      { name: 'form', required: false, description: 'Template name/id' },
      { name: 'invoice_no', required: true, description: 'Grouping key - rows with same invoice_no are grouped together' },
      { name: 'vendor_invoice_no', required: true, description: 'Vendor invoice number (stored in DB)' },
      { name: 'invoice_date', required: true, description: 'DD/MM/YYYY format' },
      { name: 'vendor', required: true, description: 'code/name' },
      { name: 'po_number', required: false, description: 'PO number - auto-fetches po_id and total amount' },
      { name: 'posting_date', required: true, description: 'Required for fiscal period calculation' },
      { name: 'subsidiary', required: true, description: 'id/name' },
      { name: 'location', required: false, description: 'id/name' },
      { name: 'ship_to', required: false, description: 'id/name' },
      { name: 'shipping_address', required: true },
      { name: 'ap_account', required: true, description: 'Fallback to vendor default' },
      { name: 'remarks', required: false },
      { name: 'memo', required: false },
      { name: 'gst_rcm', required: false, description: 'yes/no - If yes, apply RCM logic' },
      { name: 'place_of_supply', required: true, description: 'code/name - auto-determines tax_type (compares subsidiary state vs place_of_supply)' },
      { name: 'shipping_date', required: false, description: 'DD/MM/YYYY format' },
      { name: 'bill_to_address', required: true },
      { name: 'terms', required: false, description: 'Payment terms name/id - If provided, auto-calculates due_date from invoice_date + terms days' },
      { name: 'tax_detail_override', required: false, description: '1 = Use provided subsidiary_gst_number and vendor_gst_number (validates they exist); 0 or empty = Use defaults' },
      { name: 'subsidiary_gst_number', required: false, description: 'Required when tax_detail_override=1. Must exist in subsidiary tax information' },
      { name: 'vendor_gst_number', required: false, description: 'Required when tax_detail_override=1. Must exist in vendor tax information' },

      // Item Level Fields - Conditionally required (backend validates)
      { name: 'item_code', required: true, description: 'code/name' },
      { name: 'item_description', required: false },
      { name: 'item_quantity', required: true },
      { name: 'item_units', required: false, description: 'id/name' },
      { name: 'item_mrp', required: false },
      { name: 'item_rate', required: true, description: 'Used for calculation' },
      { name: 'item_discount_percent', required: false },
      { name: 'item_tax_rate', required: true, description: 'GST % (0, 5, 12, 18, 28)' },
      { name: 'item_tds_section', required: false, description: 'Auto-fetch TDS rate' },
      { name: 'item_lob', required: false, description: 'Line of business id/name' },
      { name: 'item_product', required: false, description: 'id/name' },
      { name: 'item_project', required: false, description: 'id/name' },
      { name: 'item_location', required: false, description: 'id/name' },
      { name: 'item_product_category', required: true, description: 'id/name' },
      { name: 'item_department', required: false, description: 'Custom field - department name' },
      // Expense Level Fields - Conditionally required (backend validates)
      { name: 'expense_account', required: true, description: 'id/name' },
      { name: 'expense_description', required: false },
      { name: 'expense_amount', required: true },
      { name: 'expense_discount_percent', required: false, description: 'Discount percentage' },
      { name: 'expense_india_tax_nature', required: false, description: 'Nature of goods id/name' },
      { name: 'expense_tax_rate', required: true, description: 'GST % (0, 5, 12, 18, 28)' },
      { name: 'expense_tds_section', required: false, description: 'id/name' },
      { name: 'expense_lob', required: false, description: 'Line of business id/name' },
      { name: 'expense_product', required: false, description: 'id/name' },
      { name: 'expense_project', required: false, description: 'id/name' },
      { name: 'expense_location', required: false, description: 'id/name' },
      { name: 'expense_product_category', required: true, description: 'id/name' },
    ],

    'grn': [
      { name: 'GRN Number', required: true },
      { name: 'PO Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'GRN Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Received Quantity', required: true },
      { name: 'Accepted Quantity', required: false },
      { name: 'Rejected Quantity', required: false }
    ],
    'vendor-payment': [
      { name: 'payment_no', required: true, description: 'Serial number - groups rows into one payment (if ID is not provided)' },
      { name: 'id', required: false, description: 'Existing payment ID (for adding mappings to existing payment or identifying group)' },
      { name: 'form_template', required: false, description: 'Form template name (optional)' },
      { name: 'vendor_name', required: true, description: 'code/name' },
      { name: 'payment_amount', required: true, description: 'Total payment amount' },
      { name: 'payment_date', required: true },
      { name: 'payment_method', required: true },
      { name: 'subsidiary', required: true, description: 'id/name' },
      { name: 'bank_account', required: true, description: 'code/id/name' },
      { name: 'posting_date', required: true, description: 'Required for fiscal period calculation' },
      { name: 'location', required: true, description: 'id/name' },
      { name: 'invoice_no', required: false, description: 'vendor_invoice_no or IRN' },
      { name: 'applied_amount', required: false, description: 'Amount applied to this invoice' },
    ],

    'vendor-return': [
      { name: 'Return Number', required: true },
      { name: 'Vendor Name', required: true },
      { name: 'Return Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Return Quantity', required: true },
      { name: 'Return Reason', required: false }
    ],
    'vendor-debit-note': [
      // PRIMARY GROUPING KEY - for update mode
      { name: 'id', required: false },                 // ID provided = update existing
      { name: 'debit_note_no', required: false },      // Empty = create new, provided = update existing
      // Grouping keys for item validation
      { name: 'invoice_no', required: false },         // Either invoice_no OR vendor_return_no required
      { name: 'vendor_return_no', required: false },
      // Header fields
      { name: 'vendor_name', required: true },
      { name: 'date', required: true },
      { name: 'due_date', required: false },
      { name: 'posting_date', required: true },
      { name: 'subsidiary', required: true },
      { name: 'default_payable_account', required: true },
      // REMOVED: debit_note_amount, tax - auto-calculated from items/expenses
      { name: 'reason_for_issuing_debit_note', required: false },
      { name: 'location', required: false },
      // Details fields - REMOVED: subsidiary_gst_number, vendor_gst_number - auto-fetched
      { name: 'place_of_supply', required: true },     // MANDATORY - determines tax_type
      // Item fields
      { name: 'item_code', required: false },
      { name: 'item_description', required: false },
      { name: 'item_billed_quantity', required: false }, // Renamed from item_quantity
      { name: 'item_scheme', required: false },
      { name: 'item_pack', required: false },
      { name: 'item_rate', required: false },
      { name: 'item_mrp', required: false },
      { name: 'item_units', required: false },
      { name: 'item_product_category', required: false },
      { name: 'item_discount_percentage', required: false },
      { name: 'item_tax_rate', required: false },
      // REMOVED: item_tax_type - auto-determined from place_of_supply
      { name: 'item_location', required: false },
      { name: 'item_line_of_business', required: false },
      { name: 'item_project', required: false },
      { name: 'item_product', required: false },
      { name: 'item_tds_rate', required: false },
      { name: 'item_hsn_code', required: false },
      { name: 'item_serial_no', required: false },
      { name: 'item_expiration_date', required: false },
      // Expense fields
      { name: 'expense_account', required: false },
      { name: 'expense_description', required: false },
      { name: 'expense_amount', required: false },
      { name: 'expense_discount_percentage', required: false },
      { name: 'expense_tax_rate', required: false },
      // REMOVED: expense_tax_type - auto-determined from place_of_supply
      { name: 'expense_location', required: false },
      { name: 'expense_line_of_business', required: false },
      { name: 'expense_project', required: false },
      { name: 'expense_product', required: false },
      { name: 'expense_product_category', required: false }
    ],
    'client': [
      { name: 'client_name', required: true },
      { name: 'custom_form', required: false, description: 'Form template ID or name (optional, uses default if not provided)' },
      { name: 'client_type', required: false },
      { name: 'client_category_name', required: false },
      { name: 'line_of_business', required: false },
      { name: 'gender', required: false },
      { name: 'default_receivable_account_name', required: true },
      { name: 'asset_account_name', required: false },
      { name: 'opening_balance', required: false },
      { name: 'opening_balance_date', required: false },
      { name: 'bank_account_number', required: false },
      { name: 'bank_name', required: false, description: 'Bank name for financial details' },
      { name: 'bank_account_type', required: false },
      { name: 'bank_account_ifsccode', required: false },
      { name: 'bank_account_branch', required: false },
      { name: 'bank_account_city', required: false },
      { name: 'bank_account_state', required: false },
      { name: 'bank_account_country', required: false },
      { name: 'client_group_name', required: false },
      { name: 'client_email', required: false },
      { name: 'client_phone', required: false },
      { name: 'pan_number', required: false },
      { name: 'registration_type', required: false },
      { name: 'gst_number', required: false },
      { name: 'address', required: false },
      { name: 'pin', required: false },
      { name: 'city_name', required: false },
      { name: 'state_name', required: false, description: 'State name (or use "state" as alias)' },
      { name: 'state', required: false, description: 'State (alias for state_name)' },
      { name: 'country_name', required: false },
      { name: 'shipping_address', required: false },
      { name: 'shipping_pin', required: false },
      { name: 'shipping_city_name', required: false },
      { name: 'shipping_state_name', required: false },
      { name: 'shipping_country_name', required: false },
      { name: 'subsidiary_name', required: true },
      { name: 'credit_limit', required: false }
    ],
    'client-order': [
      { name: 'id', required: false, description: 'Existing order ID (for adding items to existing order)' },
      { name: 'order_no', required: true, description: 'Order number (for grouping multiple items in new orders)' },
      { name: 'so_date', required: false, description: 'Sales order date (DD/MM/YYYY)' },
      { name: 'client_code', required: true, description: 'Client code or name' },
      { name: 'memo', required: false, description: 'Order memo/notes' },
      { name: 'subsidiary', required: true, description: 'Subsidiary name' },
      { name: 'location', required: false, description: 'Location name' },
      { name: 'ship_to', required: false, description: 'Ship to address ID/name' },
      { name: 'place_of_supply', required: true, description: 'Place of supply (state)' },
      { name: 'shipping_date', required: false, description: 'Shipping date' },
      { name: 'shipping_address', required: false, description: 'Shipping address' },
      { name: 'bill_to_address', required: false, description: 'Billing address' },
      { name: 'bill_to', required: false, description: 'Bill to (billing address ID)' },
      { name: 'shipping_carrier', required: false, description: 'Shipping carrier' },
      { name: 'shipping_method', required: false, description: 'Shipping method' },
      { name: 'project', required: false, description: 'Header-level project id/name' },
      { name: 'product', required: false, description: 'Header-level product id/name' },
      // Order items
      { name: 'item_code', required: true, description: 'Item code (product_code or name)' },
      { name: 'item_description', required: false, description: 'Item description' },
      { name: 'item_quantity', required: true, description: 'Order quantity' },
      { name: 'item_pack', required: false, description: 'Pack size (default: 1)' },
      { name: 'item_units', required: false, description: 'Unit of measure' },
      { name: 'item_scheme', required: false, description: 'Scheme/free quantity' },
      { name: 'item_mrp', required: false, description: 'Maximum Retail Price' },
      { name: 'item_rate', required: true, description: 'Item rate/price' },
      { name: 'item_discount_percentage', required: false, description: 'Discount percentage' },
      { name: 'item_india_tax_nature', required: false, description: 'Tax nature (CGST+SGST, IGST)' },
      { name: 'item_tax_rate', required: false, description: 'Tax rate (0, 5, 12, 18, 28)' },
      { name: 'item_line_of_business', required: false, description: 'Line of business' },
      { name: 'item_project', required: false, description: 'Project id/name' },
      { name: 'item_product', required: false, description: 'Product id/name' },
      { name: 'item_product_category', required: false, description: 'Product category' },
      { name: 'item_location', required: false, description: 'Item location id/name' },
      { name: 'item_department', required: false, description: 'Item department (custom field)' },
    ],
    'sales-order': [
      { name: 'id', required: false, description: 'Existing order ID (for adding items to existing order)' },
      { name: 'order_no', required: true, description: 'Order number (for grouping multiple items in new orders)' },
      { name: 'so_date', required: false, description: 'Sales order date (DD/MM/YYYY)' },
      { name: 'client_code', required: true, description: 'Client code or name' },
      { name: 'memo', required: false, description: 'Order memo/notes' },
      { name: 'subsidiary', required: true, description: 'Subsidiary name' },
      { name: 'location', required: false, description: 'Location name' },
      { name: 'ship_to', required: false, description: 'Ship to address ID/name' },
      { name: 'place_of_supply', required: true, description: 'Place of supply (state)' },
      { name: 'shipping_date', required: false, description: 'Shipping date' },
      { name: 'shipping_address', required: false, description: 'Shipping address' },
      { name: 'bill_to_address', required: false, description: 'Billing address' },
      { name: 'bill_to', required: false, description: 'Bill to (billing address ID)' },
      { name: 'shipping_carrier', required: false, description: 'Shipping carrier' },
      { name: 'shipping_method', required: false, description: 'Shipping method' },
      { name: 'project', required: false, description: 'Header-level project id/name' },
      { name: 'product', required: false, description: 'Header-level product id/name' },
      // Order items
      { name: 'item_code', required: true, description: 'Item code (product_code or name)' },
      { name: 'item_description', required: false, description: 'Item description' },
      { name: 'item_quantity', required: true, description: 'Order quantity' },
      { name: 'item_pack', required: false, description: 'Pack size (default: 1)' },
      { name: 'item_units', required: false, description: 'Unit of measure' },
      { name: 'item_scheme', required: false, description: 'Scheme/free quantity' },
      { name: 'item_mrp', required: false, description: 'Maximum Retail Price' },
      { name: 'item_rate', required: true, description: 'Item rate/price' },
      { name: 'item_discount_percentage', required: false, description: 'Discount percentage' },
      { name: 'item_india_tax_nature', required: false, description: 'Tax nature (CGST+SGST, IGST)' },
      { name: 'item_tax_rate', required: false, description: 'Tax rate (0, 5, 12, 18, 28)' },
      { name: 'item_line_of_business', required: false, description: 'Line of business' },
      { name: 'item_project', required: false, description: 'Project id/name' },
      { name: 'item_product', required: false, description: 'Product id/name' },
      { name: 'item_product_category', required: false, description: 'Product category' },
      { name: 'item_location', required: false, description: 'Item location id/name' },
      { name: 'item_department', required: false, description: 'Item department (custom field)' },
    ],
    'client-invoice': [
      // Header Level Fields
      { name: 'id', required: false, description: 'Internal ID for updates' },
      { name: 'custom_form', required: false, description: 'Form template name (e.g. PSP)' },
      { name: 'invoice_no', required: true, description: 'Invoice number' },
      { name: 'invoice_date', required: true, description: 'DD/MM/YYYY format' },
      { name: 'client', required: true, description: 'Client code/name' },
      { name: 'so_no', required: false, description: 'Sales order number' },
      { name: 'due_date', required: false, description: 'Due date' },
      { name: 'ship_to', required: false, description: 'Ship to address ID/name' },
      { name: 'subsidiary', required: true, description: 'Subsidiary id/name' },
      { name: 'location', required: true, description: 'Location id/name' },
      { name: 'memo', required: false, description: 'Memo/notes' },
      { name: 'ar_account', required: false, description: 'AR account code/name (optional - uses customer saved AR account if not provided)' },
      { name: 'posting_date', required: true, description: 'Posting date - auto-calculates posting_period' },
      { name: 'nature_of_document', required: true, description: 'Nature of document name or ID (e.g. Tax Invoice, Bill of Supply)' },
      { name: 'place_of_supply', required: false, description: 'Place of supply - auto-populated from client GST if not provided' },
      { name: 'type_of_supply', required: false, description: 'Type of Supply (e.g. B2B, B2C, Export) - optional' },
      { name: 'IRIS / CAS ID', required: false, description: 'IRIS / CAS ID' },
      { name: 'shipping_address', required: false, description: 'Shipping address' },
      { name: 'shipping_date', required: false, description: 'Shipping date (DD/MM/YYYY)' },
      { name: 'bill_to_address', required: false, description: 'Billing address' },

      // Item Level Fields
      { name: 'item_code', required: true, description: 'Item code/name' },
      { name: 'item_description', required: false, description: 'Item description' },
      { name: 'item_quantity', required: true, description: 'Quantity' },
      { name: 'item_rate', required: true, description: 'Item rate/price' },
      { name: 'item_discount_perc', required: false, description: 'Discount percentage' },
      { name: 'item_tax_rate', required: false, description: 'Tax rate (0, 5, 12, 18, 28)' },
      { name: 'item_lob', required: true, description: 'Line of business id/name' },
      { name: 'item_product', required: false, description: 'Product id/name' },
      { name: 'item_project', required: false, description: 'Project id/name' },
      { name: 'item_product_categ', required: true, description: 'Product category id/name' },
      { name: 'item_location', required: false, description: 'Item location id/name' }
    ],
    'client-return': [
      { name: 'Return Number', required: true },
      { name: 'Client Name', required: true },
      { name: 'Return Date', required: true },
      { name: 'Item Name', required: true },
      { name: 'Return Quantity', required: true },
      { name: 'Return Reason', required: false }
    ],
    'client-credit-note': [
      { name: 'id', required: false },
      { name: 'credit_note_no', required: false },
      { name: 'custom_form', required: false, description: 'Form template name (e.g. PSP)' },
      { name: 'client', required: true },
      { name: 'date', required: true },
      { name: 'return_no', required: false },
      { name: 'client_invoice', required: false },
      { name: 'order_no', required: false, description: 'Order number (saved as custom field)' },
      { name: 'subsidiary', required: true },
      { name: 'location', required: false },
      { name: 'receivable_account', required: true },
      { name: 'due_date', required: false },
      { name: 'posting_date', required: true },
      { name: 'place_of_supply', required: false, description: 'Auto-populated from client GST if not provided' },
      { name: 'item', required: true },
      { name: 'description', required: false },
      { name: 'cn_quantity', required: true },
      { name: 'item_rate', required: false, description: 'Item rate/price' },
      { name: 'item_discount_perc', required: false, description: 'Discount percentage' },
      { name: 'item_tax_rate', required: false, description: 'Tax rate (0, 5, 12, 18, 28)' },
      { name: 'item_line_of_business', required: true, description: 'Line of business id/name' },
      { name: 'item_product', required: false, description: 'Product id/name' },
      { name: 'item_project', required: false, description: 'Project id/name' },
      { name: 'item_product_category', required: true, description: 'Product category id/name' },
      { name: 'item_location', required: false, description: 'Item location id/name' },
      { name: 'item_hsn_code', required: false, description: 'HSN/SAC code' },
      { name: 'reason_for_credit_note', required: false }
    ],
    'credit-note': [
      { name: 'credit_note_no', required: false },
      { name: 'custom_form', required: false, description: 'Form template name (e.g. PSP)' },
      { name: 'client', required: true },
      { name: 'date', required: true },
      { name: 'return_no', required: false },
      { name: 'client_invoice', required: false },
      { name: 'order_no', required: false, description: 'Order number (saved as custom field)' },
      { name: 'subsidiary', required: true },
      { name: 'location', required: false },
      { name: 'receivable_account', required: false },
      { name: 'due_date', required: false },
      { name: 'posting_date', required: true },
      { name: 'place_of_supply', required: false, description: 'Auto-populated from client GST if not provided' },
      { name: 'item', required: true },
      { name: 'description', required: false },
      { name: 'cn_quantity', required: true },
      { name: 'item_rate', required: false, description: 'Item rate/price' },
      { name: 'item_discount_perc', required: false, description: 'Discount percentage' },
      { name: 'item_tax_rate', required: false, description: 'Tax rate (0, 5, 12, 18, 28)' },
      { name: 'item_line_of_business', required: true, description: 'Line of business id/name' },
      { name: 'item_product', required: false, description: 'Product id/name' },
      { name: 'item_project', required: false, description: 'Project id/name' },
      { name: 'item_product_category', required: true, description: 'Product category id/name' },
      { name: 'item_location', required: false, description: 'Item location id/name' },
      { name: 'item_india_tax_nature', required: false, description: 'Nature of goods id/name' },
      { name: 'item_hsn_code', required: false, description: 'HSN/SAC code' },
      { name: 'reason_for_credit_note', required: false }
    ],
    'client-payment': [
      { name: 'payment_no', required: true, description: 'Serial number - groups rows into one payment (if ID is not provided)' },
      { name: 'id', required: false, description: 'Existing payment ID (for adding mappings to existing payment or identifying group)' },
      { name: 'form_template', required: false, description: 'Form template name (optional)' },
      { name: 'client_name', required: true, description: 'code/name' },
      { name: 'payment_amount', required: true, description: 'Total payment amount' },
      { name: 'payment_date', required: true },
      { name: 'payment_method', required: true },
      { name: 'subsidiary', required: true, description: 'id/name' },
      { name: 'bank_account', required: true, description: 'code/id/name' },
      { name: 'posting_date', required: true },
      { name: 'location', required: false, description: 'id/name' },
      { name: 'ar_account', required: true },
      { name: 'invoice_no', required: false, description: 'client_invoice_no or IRN' },
      { name: 'applied_amount', required: false, description: 'Amount applied to this invoice' },
      { name: 'bank_transaction_no', required: false }
    ],
    'item': [
      { name: 'name', required: true },
      { name: 'product_code', required: false },
      { name: 'description', required: false },
      { name: 'category_name', required: false },
      { name: 'nature', required: false },
      { name: 'preferred_vendor_name', required: false },
      { name: 'manufacturer_name', required: false },
      { name: 'hsn_code', required: true },
      { name: 'item_weight', required: false },
      { name: 'weight_measure', required: false },
      { name: 'income_account_name', required: true },
      { name: 'asset_account_name', required: false },
      { name: 'cogs_account_name', required: false },
      { name: 'sku', required: false },
      { name: 'type_name', required: true },
      { name: 'packaging_name', required: false },
      { name: 'composition', required: false },
      { name: 'gst_percentage', required: false },
      { name: 'packing_unit', required: false },
      { name: 'lead_time_value', required: false },
      { name: 'storage_option_name', required: false },
      { name: 'expiry_category_name', required: false },
      { name: 'preferred_stock_level', required: false },
      { name: 'preferred_stock_quantity', required: false },
      { name: 'preferred_stock_day', required: false },
      { name: 'reorder_point_alert', required: false },
      { name: 'transfer_price', required: false },
      { name: 'safety_stock', required: false },
      { name: 'safety_stock_days', required: false },
      { name: 'expected_damage_change', required: false },
      { name: 'minimum_quantity', required: false },
      { name: 'maximum_quantity', required: false },
      { name: 'mrp', required: false },
      { name: 'discount_percent', required: false }
    ],
    'inventory': [
      { name: 'vendor_code', required: true, description: 'Vendor code/name - used for grouping rows' },
      { name: 'subsidiary', required: false, description: 'Subsidiary name or ID' },
      { name: 'location', required: false, description: 'Location name or ID' },
      { name: 'name', required: false, description: 'GRN name' },
      { name: 'date', required: false, description: 'DD/MM/YYYY format' },
      { name: 'item_code', required: true, description: 'Item code' },
      { name: 'display_name', required: false, description: 'Item display name' },
      { name: 'item_rate', required: false, description: 'Item rate' },
      { name: 'quantity', required: true, description: 'Quantity' },
      { name: 'batch_number', required: false, description: 'Batch number' },
    ],
    'journal-voucher': [
      { name: 'transaction_no', required: true, description: 'Used for grouping rows into one JV - not saved to DB (auto-generated)' },
      { name: 'posting_date', required: true, description: 'DD/MM/YYYY format - auto-calculates posting_period' },
      { name: 'subsidiary', required: true, description: 'Subsidiary name or ID' },
      { name: 'location', required: false, description: 'Header-level location (non-mandatory)' },
      { name: 'memo', required: false, description: 'Header memo/notes' },
      { name: 'reversal_date', required: false, description: 'Optional reversal date (DD/MM/YYYY)' },
      { name: 'account', required: true, description: 'Account name or code' },
      { name: 'debit', required: false, description: 'Debit amount (either debit or credit required per line)' },
      { name: 'credit', required: false, description: 'Credit amount (either debit or credit required per line)' },
      { name: 'line_of_business', required: true, description: 'Line of business name or ID (mandatory)' },
      { name: 'product_category', required: true, description: 'Product category name or ID (mandatory)' },
      { name: 'line_location', required: false, description: 'Line-level location (non-mandatory)' },
      { name: 'project', required: false, description: 'Project name or ID (optional)' },
      { name: 'product', required: false, description: 'Product name or ID (optional)' },
      { name: 'nature_of_good', required: false, description: 'Nature of good name or ID (optional)' },
      { name: 'department', required: false, description: 'Department name (optional - saved as custom field)' },
      { name: 'vendor_code', required: false, description: 'Vendor code/name - required if using AP account' },
      { name: 'client_code', required: false, description: 'Client code/name - required if using AR account' },
      { name: 'remarks', required: false, description: 'Line remarks' }
    ],
    'bmw-container': [
      { name: 'container_code', required: true },
      { name: 'waste_category', required: true, description: 'Category name (Yellow, Red, White, Blue) or ID (1, 2, 3, 4)' },
    ]
  };

  /**
   * Get default columns for a module
   */
  getDefaultColumns(module: string): TemplateColumnConfig[] {
    return this.moduleTemplates[module] || [];
  }

  /**
   * Download CSV template for a module
   * @param module - Module name
   * @param columns - Array of selected column names (if empty, uses all default columns)
   */
  downloadTemplate(module: string, columns: string[] = []): void {
    // Special handling for vendor-debit-note and client-credit-note
    if (module === 'vendor-debit-note' || module === 'client-credit-note' || module === 'credit-note') {
      if (module === 'vendor-debit-note') {
        this.downloadVendorDebitNoteMultiSheetTemplate();
      } else {
        this.downloadClientCreditNoteMultiSheetTemplate(module);
      }
      return;
    }

    const moduleColumns = this.getDefaultColumns(module);
    
    // Use provided columns or default columns
    let selectedColumns: TemplateColumnConfig[];
    
    if (columns.length > 0) {
      // Use only the selected columns, maintaining their order
      selectedColumns = columns
        .filter(colName => 
          moduleColumns.some(col => col.name === colName)
        )
        .map(colName => {
          const colConfig = moduleColumns.find(col => col.name === colName);
          return colConfig || { name: colName, required: false };
        });
    } else {
      // Use all default columns if none specified
      selectedColumns = moduleColumns;
    }

    if (selectedColumns.length === 0) {
      console.warn('No columns available for download');
      return;
    }

    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    // Add header row with red cell background for required fields
    selectedColumns.forEach((col, index) => {
      const cell = worksheet.getCell(1, index + 1);
      
      if (col.required) {
        // For required fields, add asterisk and red cell background
        cell.value = `${col.name} *`;
        
        // Set red background - ARGB format: Alpha (FF), Red (FF), Green (00), Blue (00)
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF0000' } // Pure red background
        };
        
        // Set white text for better contrast on red background
        cell.font = {
          color: { argb: 'FFFFFFFF' }, // White text
          bold: true
        };
        
        // Set alignment
        cell.alignment = { 
          horizontal: 'left', 
          vertical: 'middle',
          wrapText: false
        };
      } else {
        cell.value = col.name;
        cell.font = {
          color: { argb: 'FF000000' }, // Black text
          bold: false
        };
        cell.alignment = { 
          horizontal: 'left', 
          vertical: 'middle'
        };
      }
      
      // Set column width
      worksheet.getColumn(index + 1).width = 20;
    });

    // Generate filename
    const filename = `${module}_template_${new Date().toISOString().slice(0, 10)}.xlsx`;

    // Write file
    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }).catch((error: any) => {
      console.error('Error generating Excel file:', error);
      alert('Error generating Excel template. Please try again.');
    });
  }

  /**
   * Download multi-sheet Excel template for vendor-debit-note
   * Sheet 1: Items (grouping keys + header + item fields)
   * Sheet 2: Expenses (grouping keys + header + expense fields)
   * Sheet 3: Apply (grouping keys + apply fields)
   */
  private downloadVendorDebitNoteMultiSheetTemplate(): void {
    const workbook = new ExcelJS.Workbook();

    // Common header fields (grouping keys + main fields)
    const groupingKeys: TemplateColumnConfig[] = [
      { name: 'id', required: false },              // PRIMARY KEY - for update mode (ID >> debit_note_no)
      { name: 'debit_note_no', required: false }, // SECONDARY GROUPING KEY
      { name: 'invoice_no', required: false },
      { name: 'vendor_return_no', required: false },
    ];

    const headerFields: TemplateColumnConfig[] = [
      { name: 'vendor_name', required: true },
      { name: 'date', required: true },
      { name: 'due_date', required: false },
      { name: 'posting_date', required: true },
      { name: 'subsidiary', required: true },
      { name: 'default_payable_account', required: true },
      { name: 'reason_for_issuing_debit_note', required: false },
      { name: 'location', required: false },
    ];

    const detailsFields: TemplateColumnConfig[] = [
      { name: 'place_of_supply', required: true }, // MANDATORY
    ];

    // Item-specific fields
    const itemFields: TemplateColumnConfig[] = [
      { name: 'item_code', required: false },
      { name: 'item_description', required: false },
      { name: 'item_billed_quantity', required: false },
      { name: 'item_scheme', required: false },
      { name: 'item_pack', required: false },
      { name: 'item_rate', required: false },
      { name: 'item_mrp', required: false },
      { name: 'item_units', required: false },
      { name: 'item_product_category', required: false },
      { name: 'item_discount_percentage', required: false },
      { name: 'item_tax_rate', required: false },
      { name: 'item_location', required: false },
      { name: 'item_line_of_business', required: false },
      { name: 'item_project', required: false },
      { name: 'item_product', required: false },
      { name: 'item_tds_rate', required: false },
      { name: 'item_hsn_code', required: false },
      { name: 'item_serial_no', required: false },
      { name: 'item_expiration_date', required: false },
    ];

    // Expense-specific fields
    const expenseFields: TemplateColumnConfig[] = [
      { name: 'expense_account', required: false },
      { name: 'expense_description', required: false },
      { name: 'expense_amount', required: false },
      { name: 'expense_discount_percentage', required: false },
      { name: 'expense_tax_rate', required: false },
      { name: 'expense_location', required: false },
      { name: 'expense_line_of_business', required: false },
      { name: 'expense_project', required: false },
      { name: 'expense_product', required: false },
      { name: 'expense_product_category', required: false },
    ];

    // Apply-specific fields
    const applyFields: TemplateColumnConfig[] = [
      { name: 'apply_invoice_no', required: false },
      { name: 'apply_amount', required: false },
    ];

    // Helper function to add header row to a worksheet
    const addHeaderRow = (worksheet: ExcelJS.Worksheet, columns: TemplateColumnConfig[]) => {
      columns.forEach((col, index) => {
        const cell = worksheet.getCell(1, index + 1);
        
        if (col.required) {
          cell.value = `${col.name} *`;
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFF0000' }
          };
          cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        } else {
          cell.value = col.name;
          cell.font = { color: { argb: 'FF000000' }, bold: false };
        }
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        worksheet.getColumn(index + 1).width = 22;
      });
    };

    // Single Sheet: All data combined (Items + Expenses)
    const dataSheet = workbook.addWorksheet('Debit Notes');
    const allColumns = [...groupingKeys, ...headerFields, ...detailsFields, ...itemFields, ...expenseFields];
    addHeaderRow(dataSheet, allColumns);

    // Sheet 2: Apply (COMMENTED OUT for now - payment integration pending)
    // const applySheet = workbook.addWorksheet('Apply');
    // const applyColumns = [...groupingKeys, ...headerFields, ...applyFields];
    // addHeaderRow(applySheet, applyColumns);

    // Generate filename
    const filename = `vendor_debit_note_template_${new Date().toISOString().slice(0, 10)}.xlsx`;

    // Write file
    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }).catch((error: any) => {
      console.error('Error generating multi-sheet Excel file:', error);
      alert('Error generating Excel template. Please try again.');
    });
  }

  /**
   * Download multi-sheet Excel template for client-credit-note
   */
  private downloadClientCreditNoteMultiSheetTemplate(module: string = 'client-credit-note'): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Credit Notes');
    const columns = this.getDefaultColumns(module);

    columns.forEach((col, index) => {
      const cell = worksheet.getCell(1, index + 1);
      if (col.required) {
        cell.value = `${col.name} *`;
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF0000' }
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      } else {
        cell.value = col.name;
        cell.font = { color: { argb: 'FF000000' }, bold: false };
      }
      cell.alignment = { horizontal: 'left', vertical: 'middle' };
      worksheet.getColumn(index + 1).width = 22;
    });

    const filename = `client_credit_note_template_${new Date().toISOString().slice(0, 10)}.xlsx`;

    workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }).catch((error: any) => {
      console.error('Error generating client credit note template:', error);
      alert('Error generating template. Please try again.');
    });
  }

  /**
   * Update module template configuration
   */
  updateModuleTemplate(module: string, columns: TemplateColumnConfig[]): void {
    this.moduleTemplates[module] = columns;
  }
}

